import type { LinkedClinic } from '../hooks/usePetLinkedClinics'

const getClinicOrHospitalName = (value: any): string | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const candidates = [
    value.clinic_name,
    value.clinicName,
    value.hospital_name,
    value.hospitalName,
    value.clinic?.name,
    value.hospital?.name,
  ]

  const match = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim())
  return typeof match === 'string' ? match.trim() : null
}

/**
 * Legacy owner-based resolution. Two known blind spots (the repeat "Vet
 * Account is blank" reports): owner.vets elements that arrive as raw IDs
 * instead of hydrated objects, and pets linked to a clinic only via
 * vet-shares (no owner.vets entry at all). Prefer
 * resolveVetClinicName() with the authoritative map; this remains as the
 * fallback when the /pets/linked-clinics fetch is unavailable.
 */
export const getVetClinicName = (record: any, emptyValue: string): string => {
  const owner = record?.owner
  if (!owner) {
    return emptyValue
  }

  if (owner.role === 'vet') {
    return getClinicOrHospitalName(owner) || emptyValue
  }

  if (owner.vets && Array.isArray(owner.vets) && owner.vets.length > 0) {
    const firstVet = owner.vets.find((vet: any) => typeof vet === 'object')
    return getClinicOrHospitalName(firstVet) || emptyValue
  }

  return getClinicOrHospitalName(owner) || emptyValue
}

/** Display name for one linked clinic: clinic/hospital name, else contact name/email. */
export const getLinkedClinicDisplayName = (clinic: LinkedClinic): string | null => {
  const candidates = [clinic.clinic_name, clinic.hospital_name, clinic.name, clinic.email]
  const match = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim())
  return typeof match === 'string' ? match.trim() : null
}

/**
 * Resolve the "Vet Account" display for a pet, preferring the authoritative
 * pet→clinics map from /pets/linked-clinics (usePetLinkedClinics) and falling
 * back to the legacy owner-based resolution when the map is unavailable
 * (null) or has no entry for this pet.
 *
 * A pet linked to several clinics shows the primary one plus a "+N" suffix;
 * use the full list (e.g. in a tooltip) for the rest.
 */
export const resolveVetClinicName = (
  linkedClinics: ReadonlyMap<number, LinkedClinic[]> | null,
  record: any,
  emptyValue: string,
): string => {
  const petId = record?.id
  if (linkedClinics && Number.isInteger(petId)) {
    const clinics = linkedClinics.get(petId)
    if (clinics && clinics.length > 0) {
      const first = getLinkedClinicDisplayName(clinics[0])
      if (first) {
        return clinics.length > 1 ? `${first} (+${clinics.length - 1})` : first
      }
    }
    // Map is authoritative: present-but-no-entry means "no linked clinic".
    // Still fall through to the legacy resolution so a hydrated owner.vets
    // can rescue display in case the map is stale (60s TTL) right after a
    // new linkage was created.
  }
  return getVetClinicName(record, emptyValue)
}
